import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  IconButton,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Tab,
  Tabs,
  Radio,
  CircularProgress,
  LinearProgress,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const STRATEGY_ENGINE_URL = process.env.REACT_APP_STRATEGY_ENGINE_URL || 'https://strategy-engine.houman-ghavamzadeh.workers.dev';

// Feature label mapping for display
const featureLabels = {
  max_strategies: 'Max Strategies',
  max_indicators: 'Max Indicators',
  paper_trading: 'Paper Trading',
  live_trading: 'Live Trading',
  rescue_system: 'Rescue System',
  reactive_trailing: 'Reactive Trailing',
  webhooks: 'Webhooks',
  ai_learning: 'AI Learning',
  exit_presets: 'Exit Presets',
  max_concurrent_positions: 'Max Concurrent Positions',
  max_exchanges: 'Max Exchanges',
  signal_matrix: 'Signal Matrix',
  experience_mode: 'Experience Mode',
  support_level: 'Support Level',
};

function Plans() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [tabValue, setTabValue] = useState(0);
  const [plans, setPlans] = useState([]);
  const [mySub, setMySub] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openUpgradeDialog, setOpenUpgradeDialog] = useState(false);
  const [upgradeDetails, setUpgradeDetails] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [plansRes, subRes] = await Promise.all([
          fetch(`${STRATEGY_ENGINE_URL}/api/plans`),
          fetch(`${STRATEGY_ENGINE_URL}/api/plans/my-subscription`, { headers: getAuthHeaders() }),
        ]);
        const plansData = await plansRes.json();
        const subData = await subRes.json();
        if (plansData.success) setPlans(plansData.data);
        if (subData.success) {
          setMySub(subData.data.subscription);
          setUsage(subData.data.usage);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleTabChange = (event, newValue) => { setTabValue(newValue); };
  const handleBillingCycleChange = (event) => { setBillingCycle(event.target.value); };

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const handleUpgradeClick = (plan) => { setUpgradeDetails(plan); setOpenUpgradeDialog(true); };

  const confirmUpgrade = async () => {
    if (!upgradeDetails) return;
    setCheckoutLoading(true);
    try {
      const res = await fetch(`${STRATEGY_ENGINE_URL}/api/stripe/checkout`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ planId: upgradeDetails.id, billingPeriod: billingCycle === '6month' ? 'sixMonth' : billingCycle }),
      });
      const data = await res.json();
      if (data.success && data.data?.url) {
        window.location.href = data.data.url;
      } else {
        alert(data.error || 'Failed to create checkout session');
      }
    } catch (err) {
      alert('Checkout error: ' + err.message);
    } finally {
      setCheckoutLoading(false);
      setOpenUpgradeDialog(false);
    }
  };

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await fetch(`${STRATEGY_ENGINE_URL}/api/stripe/portal`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success && data.data?.url) {
        window.location.href = data.data.url;
      } else {
        alert(data.error || 'Failed to open billing portal');
      }
    } catch (err) {
      alert('Portal error: ' + err.message);
    } finally {
      setPortalLoading(false);
    }
  };

  const currentPlanId = mySub?.planId || 'starter';

  const getPrice = (plan) => {
    if (billingCycle === 'monthly') return `$${plan.pricing.monthly}`;
    if (billingCycle === '6month') return `$${plan.pricing.sixMonth}`;
    return `$${plan.pricing.yearly}`;
  };

  const getPeriodLabel = () => {
    if (billingCycle === 'monthly') return 'month';
    if (billingCycle === '6month') return '6 months';
    return 'year';
  };

  const getFeatureList = (features) => {
    const displayOrder = ['max_strategies', 'max_indicators', 'paper_trading', 'live_trading', 'rescue_system', 'reactive_trailing', 'webhooks', 'ai_learning', 'exit_presets', 'signal_matrix', 'experience_mode', 'max_concurrent_positions', 'max_exchanges', 'support_level'];
    return displayOrder.map(key => {
      const val = features[key];
      const label = featureLabels[key] || key;
      if (val === 'true') return { text: label, included: true };
      if (val === 'false') return { text: label, included: false };
      if (key === 'support_level') return { text: `${val.charAt(0).toUpperCase() + val.slice(1)} Support`, included: true };
      const num = parseInt(val);
      if (num >= 999) return { text: `Unlimited ${label.replace('Max ', '')}`, included: true };
      return { text: `${num} ${label.replace('Max ', '')}`, included: true };
    });
  };
  
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Subscription Plans</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Subscription Plans</Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Current Plan Summary */}
      {mySub && (
        <Paper sx={{ p: 2, mb: 3, background: 'linear-gradient(135deg, rgba(0,255,148,0.05) 0%, rgba(59,130,246,0.05) 100%)' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Current Plan</Typography>
              <Typography variant="h6">{mySub.planName} <Chip label={mySub.status} size="small" color="success" sx={{ ml: 1 }} /></Typography>
              <Typography variant="caption" color="text.secondary">
                {mySub.billingPeriod} billing • Renews {mySub.currentPeriodEnd ? new Date(mySub.currentPeriodEnd).toLocaleDateString() : '—'}
              </Typography>
            </Grid>
            {usage && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Strategies</Typography>
                    <Typography variant="body2">{usage.strategies.current} / {usage.strategies.limit}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Active Runs</Typography>
                    <Typography variant="body2">{usage.activeRuns.current} / {usage.activeRuns.limit}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Exchanges</Typography>
                    <Typography variant="body2">{usage.exchanges.current} / {usage.exchanges.limit}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Plans & Pricing" />
        <Tab label="Billing & Payment" />
      </Tabs>
      
      {/* Plans & Pricing Tab */}
      {tabValue === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <FormControl>
              <RadioGroup row value={billingCycle} onChange={handleBillingCycleChange}>
                <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
                <FormControlLabel value="6month" control={<Radio />} label="6-Month" />
                <FormControlLabel value="yearly" control={<Radio />} label="Yearly" />
              </RadioGroup>
            </FormControl>
            {billingCycle === 'yearly' && <Chip label="Best Value" color="success" size="small" sx={{ ml: 1, height: 24, mt: 0.5 }} />}
          </Box>
          
          <Grid container spacing={3}>
            {plans.map((plan) => {
              const isCurrent = plan.id === currentPlanId;
              const isPopular = plan.id === 'professional';
              const currentIdx = plans.findIndex(p => p.id === currentPlanId);
              const thisIdx = plans.findIndex(p => p.id === plan.id);
              const isUpgrade = thisIdx > currentIdx;
              const features = getFeatureList(plan.features);

              return (
                <Grid item xs={12} md={4} key={plan.id}>
                  <Card 
                    variant={isPopular ? 'elevation' : 'outlined'} 
                    elevation={isPopular ? 6 : 0}
                    sx={{
                      position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
                      ...(isPopular && { borderTop: '4px solid', borderColor: 'primary.main' }),
                      ...(isCurrent && { border: '2px solid', borderColor: 'success.main' }),
                    }}
                  >
                    {isPopular && <Chip label="Most Popular" color="primary" size="small" sx={{ position: 'absolute', top: -12, right: 24 }} />}
                    {isCurrent && <Chip label="Current" color="success" size="small" sx={{ position: 'absolute', top: 12, right: 12 }} />}
                    
                    <CardContent sx={{ flex: 1 }}>
                      <Typography variant="h5" component="div" gutterBottom>{plan.name}</Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>{plan.description}</Typography>
                      
                      <Box sx={{ my: 2 }}>
                        <Typography variant="h3" component="div">{getPrice(plan)}</Typography>
                        <Typography variant="body2" color="text.secondary">per {getPeriodLabel()}</Typography>
                      </Box>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <List dense>
                        {features.map((feature, index) => (
                          <ListItem key={index} disableGutters>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              {feature.included ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="disabled" fontSize="small" />}
                            </ListItemIcon>
                            <ListItemText primary={feature.text} primaryTypographyProps={{ ...(feature.included ? {} : { color: 'text.disabled' }), variant: 'body2' }} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                    
                    <CardActions sx={{ px: 2, pb: 2 }}>
                      {isCurrent ? (
                        <Button fullWidth variant="outlined" disabled startIcon={<CheckIcon />}>Current Plan</Button>
                      ) : (
                        <Button 
                          fullWidth variant="contained" 
                          color={isUpgrade ? 'primary' : 'secondary'}
                          onClick={() => handleUpgradeClick(plan)}
                          startIcon={isUpgrade ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                        >
                          {isUpgrade ? 'Upgrade' : 'Downgrade'}
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          
          <Box sx={{ mt: 4 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Need a custom plan?</Typography>
              <Typography variant="body2" paragraph>
                For businesses with specific requirements, we offer tailored enterprise solutions. Contact our sales team.
              </Typography>
              <Button variant="outlined">Contact Sales</Button>
            </Paper>
          </Box>
        </>
      )}
      
      {/* Billing & Payment Tab */}
      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Billing & Payment</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage your subscription, update payment methods, and view invoices through the Stripe billing portal.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" startIcon={<CreditCardIcon />} onClick={openBillingPortal} disabled={portalLoading}>
              {portalLoading ? 'Opening...' : 'Open Billing Portal'}
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Upgrade/Downgrade Dialog */}
      <Dialog open={openUpgradeDialog} onClose={() => setOpenUpgradeDialog(false)} maxWidth="sm" fullWidth>
        {upgradeDetails && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Change to {upgradeDetails.name} Plan</Typography>
                <IconButton onClick={() => setOpenUpgradeDialog(false)}><CloseIcon /></IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" paragraph>
                You are about to change your plan to <strong>{upgradeDetails.name}</strong>.
              </Typography>
              <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">New Plan:</Typography>
                  <Typography variant="body2" fontWeight="bold">{upgradeDetails.name} ({getPrice(upgradeDetails)}/{getPeriodLabel()})</Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setOpenUpgradeDialog(false)}>Cancel</Button>
              <Button variant="contained" onClick={confirmUpgrade} disabled={checkoutLoading}>
                {checkoutLoading ? 'Redirecting...' : 'Proceed to Checkout'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default Plans;
